import { useCallback } from "react";
import { $api } from "@babackapp/private-api-client/generated/client";
import { injectEmbedScript } from "./inject-embed-script";

type UseOpenReturnWidgetOptions = {
  /** Preview the V3 portal even when the shop has not opted into it. */
  forceNewWidget?: boolean;
};

type ReturnWidget = {
  open: () => void;
  setSource: (source: string) => void;
};

/**
 * Entry URL of a shop's portal. The `reset` search param sits before the hash
 * so the iframe reloads the document: the portal only ever navigates its hash,
 * and a hash-only source change leaves it on the page the previous session
 * ended on. `advanced` mirrors the `data-advanced` the office injects with.
 */
function buildEntrySource(providerId: string) {
  return `${window.location.origin}/widget.html?reset=${Date.now()}#/return-widget/${providerId}?advanced=true`;
}

export function useOpenReturnWidget(
  shopifyUrl: string,
  providerId: string,
  { forceNewWidget = false }: UseOpenReturnWidgetOptions = {},
) {
  const { refetch } = $api.useQuery(
    "get",
    "/embed-html",
    {
      params: { query: { shopifyUrl, autoOpen: true } },
      enabled: false,
    },
  );

  return useCallback(async () => {
    const w = window as unknown as Record<string, unknown>;
    const widget = w.babackReturnWidget as ReturnWidget | undefined;

    // Re-injecting the embed script for another shop yields the first shop's
    // portal: createWidget registers the custom element once, and the class it
    // registers closes over the config of that first call. So an existing
    // widget is retargeted through its source, which carries the shop.
    if (widget) {
      widget.setSource(buildEntrySource(providerId));
      widget.open();
      return;
    }

    const { data } = await refetch();
    if (!data?.shopId) return;
    injectEmbedScript(data, data.shopId, {
      noOverlayClose: false,
      forceNewWidget,
    });
  }, [refetch, providerId, forceNewWidget]);
}
